<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { developerArticle, sdkNavigation } from '../app/developerDocs'
import { useSiteLocale } from '../composables/useSiteLocale'
import '../../../public-sdk/tools/docs.css'

const route = useRoute()
const router = useRouter()
const { currentLocale } = useSiteLocale()
const zh = computed(() => currentLocale.value === 'zh-CN')
const slug = computed(() => typeof route.params.slug === 'string' && route.params.slug ? route.params.slug : 'overview')
const article = computed(() => developerArticle(slug.value, currentLocale.value))
const index = computed(() => sdkNavigation.articles.findIndex(item => item.slug === slug.value))
const previous = computed(() => sdkNavigation.articles[index.value - 1])
const next = computed(() => sdkNavigation.articles[index.value + 1])

function navigateDocument(event: MouseEvent) {
  if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return
  const link = (event.target instanceof Element ? event.target : null)?.closest<HTMLAnchorElement>('a')
  const href = link?.getAttribute('href')
  if (href?.startsWith('/docs/developers/') || href?.startsWith('#')) {
    event.preventDefault()
    void router.push(href.startsWith('#') ? `${route.path}${href}` : href)
  }
}
</script>

<template>
  <div class="simple-page developer-docs-page">
    <header class="developer-docs-hero">
      <RouterLink class="back-link" to="/docs">← {{ zh ? '全部文档' : 'All docs' }}</RouterLink>
      <span class="eyebrow">MOTEXA / DEVELOPER DOCUMENTATION</span>
      <h1>{{ zh ? '主题开发文档' : 'Theme developer docs' }}</h1>
      <p>{{ zh ? '通过标准展示数据开发 App 内主题，在浏览器中调试，再构建可导入的运行包。' : 'Build App-hosted themes with normalized display data, debug in the browser, and package for App import.' }}</p>
      <div class="developer-docs-meta">SDK {{ sdkNavigation.sdkVersion }} <span>API {{ sdkNavigation.apiVersion }}</span><span>Demo {{ sdkNavigation.demoVersion }}</span></div>
      <div class="developer-docs-actions">
        <a href="/developers/debugger/mock-host.html" target="_blank" rel="noopener noreferrer">{{ zh ? '打开合成数据调试器 ↗' : 'Open synthetic debugger ↗' }}</a>
        <a :href="`/developers/downloads/Motexa-Ride-UI-SDK-${sdkNavigation.sdkVersion}-GitHub-Release.zip`" download>{{ zh ? '下载 SDK 完整开发包 ↓' : 'Download SDK development kit ↓' }}</a>
        <a href="https://github.com/shj56166/Motexa/tree/HEAD/developers/ride-ui-sdk" target="_blank" rel="noopener noreferrer">{{ zh ? '在 GitHub 查看源码与文档 ↗' : 'View source and docs on GitHub ↗' }}</a>
      </div>
    </header>
    <div class="developer-docs-layout">
      <nav class="developer-docs-nav" :aria-label="zh ? '开发文档导航' : 'Developer documentation navigation'">
        <RouterLink v-for="item in sdkNavigation.articles" :key="item.slug" :to="`/docs/developers/${item.slug}`" :aria-current="item.slug === slug ? 'page' : undefined">{{ item.title[currentLocale] }}</RouterLink>
      </nav>
      <div v-if="article" class="developer-docs-main">
        <article class="sdk-prose" @click="navigateDocument" v-html="article.html"></article>
        <nav class="developer-docs-pager" :aria-label="zh ? '相邻文章' : 'Adjacent articles'">
          <RouterLink v-if="previous" :to="`/docs/developers/${previous.slug}`">← {{ previous.title[currentLocale] }}</RouterLink><span v-else></span>
          <RouterLink v-if="next" :to="`/docs/developers/${next.slug}`">{{ next.title[currentLocale] }} →</RouterLink>
        </nav>
      </div>
      <div v-else class="developer-docs-main"><h2>{{ zh ? '没有找到这篇文档' : 'Document not found' }}</h2><RouterLink to="/docs/developers">{{ zh ? '返回开发文档' : 'Back to developer docs' }}</RouterLink></div>
      <aside v-if="article" class="developer-docs-toc" :aria-label="zh ? '本页目录' : 'On this page'">
        <strong>{{ zh ? '本页目录' : 'On this page' }}</strong>
        <RouterLink v-for="heading in article.headings" :key="heading.id" :to="`${route.path}#${heading.id}`" :class="{ 'is-subheading': heading.depth === 3 }">{{ heading.title }}</RouterLink>
      </aside>
    </div>
  </div>
</template>

<style scoped>
.developer-docs-page { max-width: 1440px; }
.developer-docs-hero { max-width: 950px; margin-bottom: 42px; }
.developer-docs-hero .back-link { display: inline-block; margin-bottom: 28px; }
.developer-docs-hero h1 { margin: 22px 0 16px; font-size: clamp(32px, 4.3vw, 58px); line-height: 1.16; letter-spacing: -.045em; font-weight: 600; }
.developer-docs-hero p { max-width: 750px; color: var(--muted); font-size: 15px; line-height: 1.8; }
.developer-docs-meta { display: flex; flex-wrap: wrap; gap: 22px; margin-top: 22px; color: #a9b6c9; font: 12px var(--mono); }
.developer-docs-actions { display: flex; flex-wrap: wrap; gap: 14px; margin-top: 24px; }
.developer-docs-actions a { display: inline-flex; align-items: center; min-height: 44px; padding: 11px 16px; border: 1px solid var(--line); border-radius: 8px; color: var(--ink); font-size: 13px; }
.developer-docs-actions a:hover { border-color: var(--accent); }
.developer-docs-layout { display: grid; grid-template-columns: 190px minmax(0, 1fr) 155px; gap: 36px; padding-top: 32px; border-top: 1px solid var(--line); }
.developer-docs-nav, .developer-docs-toc { position: sticky; top: 110px; align-self: start; display: grid; gap: 6px; font-size: 13px; }
.developer-docs-nav a { padding: 11px 13px; border-radius: 7px; line-height: 1.5; color: var(--muted); }
.developer-docs-nav a[aria-current='page'] { color: var(--ink); background: rgba(148,175,206,.12); }
.developer-docs-nav a:hover, .developer-docs-toc a:hover { color: var(--accent); }
.developer-docs-main { min-width: 0; color: #b2bac8; }
.developer-docs-toc { font-size: 12px; color: var(--muted); }
.developer-docs-toc strong { margin-bottom: 12px; color: var(--ink); }
.developer-docs-toc a { padding: 6px 0; line-height: 1.6; }
.developer-docs-toc a.is-subheading { padding-left: 12px; }
.developer-docs-pager { display: flex; justify-content: space-between; gap: 24px; margin-top: 40px; padding-top: 22px; border-top: 1px solid var(--line); font-size: 13px; color: var(--ink); }
.developer-docs-page a:focus-visible { outline: 2px solid var(--accent); outline-offset: 4px; }
@media (max-width: 1150px) { .developer-docs-layout { grid-template-columns: 180px minmax(0,1fr); gap: 26px; } .developer-docs-toc { display: none; } }
@media (max-width: 720px) { .developer-docs-layout { grid-template-columns: 1fr; gap: 25px; padding-top: 22px; } .developer-docs-nav { position: static; display: flex; flex-wrap: wrap; gap: 4px; } .developer-docs-nav a { padding: 9px 11px; font-size: 12px; } .developer-docs-hero { margin-bottom: 28px; } .developer-docs-hero p { font-size: 14px; } }
</style>
